import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import GeographicChart from '../components/GeographicChart';
import HourlyChart from '../components/HourlyChart';
import DateClicksChart from '../components/DateClicksChart';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import Popup from '../components/Popup';

// Bundler-resolved asset URLs
const ANALYTICS_ASSET = new URL('../assets/images/Analystics.png', import.meta.url).href;
const QR_ASSET = new URL('../assets/images/QRCode.png', import.meta.url).href;
const DELETE_ASSET = new URL('../assets/images/Delete.png', import.meta.url).href;
const COPY_ASSET = new URL('../assets/images/Copy.png', import.meta.url).href;

const SORT_DIRECTIONS = { ASC: 'asc', DESC: 'desc' };
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .db-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    padding: 32px 20px 80px;
    color: #e2e8f0;
  }

  .db-inner { max-width: 1440px; margin: 0 auto; }

  /* Page heading */
  .db-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 32px;
  }

  .db-title {
    font-size: 1.8rem;
    font-weight: 900;
    color: #f1f5f9;
    letter-spacing: -0.03em;
    margin: 0;
  }

  .db-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    text-decoration: none;
    padding: 11px 22px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
    box-shadow: 0 4px 16px rgba(99,102,241,0.3);
  }
  .db-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(99,102,241,0.5);
  }

  /* Alert banners */
  .db-alert {
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 14px;
    margin-bottom: 24px;
  }
  .db-alert-error   { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .db-alert-success { background: rgba(34,197,94,0.1);  border: 1px solid rgba(34,197,94,0.2);   color: #86efac; }

  /* Stat / overview row */
  .db-stat-row {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
    margin-bottom: 28px;
  }

  .db-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 20px;
    padding: 28px;
    transition: border-color 0.25s;
  }
  .db-card:hover { border-color: rgba(99,102,241,0.28); }

  .db-card-title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
    margin: 0 0 16px;
  }

  /* Stat numbers */
  .db-stat-num {
    font-size: 2.2rem;
    font-weight: 900;
    color: #f1f5f9;
    line-height: 1;
    margin-bottom: 4px;
    letter-spacing: -0.04em;
  }
  .db-stat-label { font-size: 13px; color: #475569; }

  .db-stats-inner {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .db-stat-tile {
    background: rgba(99,102,241,0.07);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 14px;
    padding: 18px 14px;
    text-align: center;
  }

  /* Plan card */
  .db-plan-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    padding: 3px 10px;
    border-radius: 100px;
    margin-bottom: 12px;
  }
  .badge-free     { background: rgba(100,116,139,0.15); color: #94a3b8; border: 1px solid rgba(100,116,139,0.2); }
  .badge-pro      { background: rgba(99,102,241,0.15);  color: #a5b4fc; border: 1px solid rgba(99,102,241,0.25); }
  .badge-business { background: rgba(236,160,28,0.12);  color: #fbbf24; border: 1px solid rgba(236,160,28,0.2); }

  .db-plan-name   { font-size: 1.15rem; font-weight: 800; color: #f1f5f9; margin: 0 0 6px; }
  .db-plan-limit  { font-size: 13px; color: #64748b; margin: 0 0 16px; }

  /* Brand settings button */
  .db-btn-brand {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(251,191,36,0.1);
    border: 1px solid rgba(251,191,36,0.25);
    color: #fbbf24;
    text-decoration: none;
    padding: 9px 18px;
    border-radius: 10px;
    font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: inherit;
    transition: all 0.2s;
  }
  .db-btn-brand:hover { background: rgba(251,191,36,0.18); }

  /* URL type badge */
  .db-url-type-badge {
    display: inline-flex; align-items: center;
    font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
    padding: 2px 8px; border-radius: 100px;
  }
  .db-url-type-path { background: rgba(251,191,36,0.1); border: 1px solid rgba(251,191,36,0.2); color: #fbbf24; }
  .db-url-type-sub  { background: rgba(99,102,241,0.12); border: 1px solid rgba(99,102,241,0.25); color: #a5b4fc; }

  .db-feature-list { list-style: none; padding: 0; margin: 0 0 20px; display: flex; flex-direction: column; gap: 7px; }
  .db-feature-list li {
    font-size: 13px;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .db-feature-list li::before {
    content: '';
    display: block;
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #6366f1;
    box-shadow: 0 0 5px #6366f1;
    flex-shrink: 0;
  }

  /* Links table section */
  .db-links-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 20px;
    padding: 28px;
    overflow: hidden;
  }

  .db-links-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
  }

  .db-links-title {
    font-size: 1.05rem;
    font-weight: 800;
    color: #f1f5f9;
    margin: 0;
  }

  /* Table */
  .db-table-wrap { overflow-x: auto; }

  table.db-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }

  .db-table thead tr {
    border-bottom: 1px solid rgba(99,102,241,0.15);
  }

  .db-table th {
    padding: 10px 14px;
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #475569;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;
  }
  .db-table th:hover { color: #a5b4fc; }

  .db-table td {
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    color: #94a3b8;
    vertical-align: middle;
  }

  .db-table tbody tr:hover td { background: rgba(99,102,241,0.04); }
  .db-table tbody tr:last-child td { border-bottom: none; }

  .db-link-short {
    color: #818cf8;
    text-decoration: none;
    font-weight: 600;
    font-size: 13px;
  }
  .db-link-short:hover { color: #a5b4fc; text-decoration: underline; }

  .db-link-long {
    color: #64748b;
    text-decoration: none;
    font-size: 13px;
    max-width: 220px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }
  .db-link-long:hover { color: #94a3b8; }

  .db-click-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    padding: 2px 10px;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 100px;
    font-size: 12.5px;
    font-weight: 700;
    color: #a5b4fc;
  }

  /* Action icon buttons */
  .db-action-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    opacity: 1;
  }
  .db-action-btn:hover { background: rgba(99,102,241,0.15); }
  .db-action-btn:disabled { opacity: 0.2; cursor: not-allowed; }
  .db-action-btn:disabled:hover { background: none; }
  .db-action-btn img { width: 22px; height: 22px; object-fit: contain; }

  .db-empty {
    text-align: center;
    padding: 60px 20px;
    color: #475569;
    font-size: 15px;
  }
  .db-empty-icon { font-size: 2.5rem; margin-bottom: 12px; }

  /* QR Modal */
  .db-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .db-modal {
    background: #131929;
    border: 1px solid rgba(99,102,241,0.25);
    border-radius: 20px;
    padding: 36px;
    max-width: 400px;
    width: 100%;
    text-align: center;
    box-shadow: 0 24px 60px rgba(0,0,0,0.6);
    animation: authFadeUp 0.25s ease-out both;
  }

  @keyframes authFadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .db-modal h3 {
    font-size: 1.2rem;
    font-weight: 800;
    color: #f1f5f9;
    margin: 0 0 8px;
  }

  .db-modal-url {
    font-size: 13px;
    color: #818cf8;
    word-break: break-all;
    margin-bottom: 20px;
  }

  .db-modal img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    border: 1px solid rgba(99,102,241,0.15);
  }

  .db-modal-hint { font-size: 12.5px; color: #475569; margin-top: 12px; margin-bottom: 20px; }

  .db-modal-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .db-modal-btn {
    padding: 9px 16px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
  }

  .db-modal-btn-copy  { background: rgba(99,102,241,0.15); color: #a5b4fc; border: 1px solid rgba(99,102,241,0.25); }
  .db-modal-btn-share { background: rgba(34,197,94,0.1);   color: #86efac; border: 1px solid rgba(34,197,94,0.2); }
  .db-modal-btn-dl    { background: rgba(251,191,36,0.1);  color: #fbbf24; border: 1px solid rgba(251,191,36,0.2); }
  .db-modal-btn-close { background: rgba(255,255,255,0.06); color: #94a3b8; border: 1px solid rgba(255,255,255,0.08); margin-top: 8px; }

  .db-modal-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }

  /* ── Links pagination ── */
  .db-links-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 4px;
    flex-wrap: wrap;
    gap: 10px;
  }
  .db-links-page-info { font-size: 13px; color: #475569; }
  .db-links-page-btns { display: flex; gap: 8px; }
  .db-links-page-btn {
    padding: 6px 15px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.18);
    color: #a5b4fc;
  }
  .db-links-page-btn:hover:not(:disabled) { background: rgba(99,102,241,0.22); }
  .db-links-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  /* Score badges */
  .db-score-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: 100px;
    font-size: 12px; font-weight: 700; white-space: nowrap;
  }
  .db-score-green  { background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.25); color: #86efac; }
  .db-score-yellow { background: rgba(234,179,8,0.12);  border: 1px solid rgba(234,179,8,0.25);  color: #fde047; }
  .db-score-red    { background: rgba(239,68,68,0.12);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .db-score-loading { background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.15); color: #64748b; font-weight: 600; }
  
  /* Status badges */
  .db-status-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 100px;
    font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em;
    transition: all 0.2s;
  }
  .db-status-badge.clickable { cursor: pointer; }
  .db-status-badge.clickable:hover { filter: brightness(1.2); transform: translateY(-1px); }
  .db-status-badge.disabled { opacity: 0.5; filter: grayscale(0.8); cursor: not-allowed; }

  .db-status-active   { background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.25); color: #86efac; }
  .db-status-inactive { background: rgba(239,68,68,0.12);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .db-status-expired  { background: rgba(234,179,8,0.12);  border: 1px solid rgba(234,179,8,0.25);  color: #fde047; }

  /* ── Click Map tabs ── */
  .db-chart-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 20px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 12px;
    padding: 4px;
    width: fit-content;
  }
  .db-chart-tab {
    padding: 7px 18px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    font-family: inherit;
    background: none;
    color: #475569;
    transition: all 0.2s;
  }
  .db-chart-tab:hover { color: #a5b4fc; }
  .db-chart-tab.active {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: #fff;
    box-shadow: 0 4px 14px rgba(99,102,241,0.35);
  }
  .db-chart-tab:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    filter: grayscale(1);
  }

  /* ── Self-Healing ── */
  .db-heal-card {
    background: rgba(16, 185, 129, 0.04);
    border: 1px solid rgba(16, 185, 129, 0.18);
    border-radius: 20px;
    padding: 24px;
  }
  .db-heal-summary { display: flex; gap: 18px; flex-wrap: wrap; margin-bottom: 12px; }
  .db-heal-pill {
    display: flex; align-items: center; gap: 7px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px; padding: 10px 16px;
    flex: 1; min-width: 110px;
  }
  .db-heal-pill-num { font-size: 1.6rem; font-weight: 900; line-height: 1; color: #f1f5f9; }
  .db-heal-pill-label { font-size: 12px; color: #64748b; font-weight: 600; }
  .db-heal-last { font-size: 12px; color: #475569; margin-top: 4px; }
  .db-heal-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border-radius: 100px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
  }
  .db-heal-healthy  { background: rgba(34,197,94,0.12);   border: 1px solid rgba(34,197,94,0.25);   color: #86efac; }
  .db-heal-toggle {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 100px;
    font-size: 11px; font-weight: 700;
    border: none; cursor: pointer; font-family: inherit;
    transition: all 0.2s;
  }
  .db-heal-toggle-on  { background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.25); color: #34d399; }
  .db-heal-toggle-off { background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);  color: #a5b4fc; }
  .db-heal-toggle:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Inline edit cells ── */
  .db-cell-edit-btn {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 9px; border-radius: 100px;
    font-size: 11.5px; font-weight: 700;
    border: none; cursor: pointer; font-family: inherit;
    transition: all 0.2s;
    background: rgba(99,102,241,0.08);
    border: 1px solid rgba(99,102,241,0.18);
    color: #818cf8;
  }
  .db-cell-edit-btn:hover { background: rgba(99,102,241,0.18); transform: translateY(-1px); }
  .db-cell-edit-btn.has-value { background: rgba(251,191,36,0.08); border-color: rgba(251,191,36,0.2); color: #fbbf24; }
  .db-cell-edit-btn.has-pw    { background: rgba(34,197,94,0.08);  border-color: rgba(34,197,94,0.2);  color: #86efac; }

  /* ── Inline edit modal ── */
  .db-ie-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center;
    z-index: 1100; padding: 20px;
  }
  .db-ie-modal {
    background: #131929;
    border: 1px solid rgba(99,102,241,0.3);
    border-radius: 20px;
    padding: 32px 28px;
    width: 100%; max-width: 380px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.6);
    animation: authFadeUp 0.22s ease-out both;
  }
  .db-ie-modal h3 { font-size: 1.1rem; font-weight: 800; color: #f1f5f9; margin: 0 0 6px; }
  .db-ie-modal .db-modal-url { margin-bottom: 18px; }
  .db-ie-input {
    width: 100%; box-sizing: border-box;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.25);
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 14px; font-family: inherit;
    color: #f1f5f9;
    outline: none;
    margin-bottom: 18px;
    transition: border-color 0.2s;
    color-scheme: dark;
  }
  .db-ie-input:focus { border-color: rgba(99,102,241,0.6); }
  .db-ie-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .db-ie-btn {
    flex: 1; padding: 9px 14px;
    border-radius: 10px; border: none;
    font-size: 13px; font-weight: 700; font-family: inherit;
    cursor: pointer; transition: all 0.2s;
  }
  .db-ie-btn-save   { background: linear-gradient(135deg,#6366f1,#4f46e5); color:#fff; }
  .db-ie-btn-save:hover { filter: brightness(1.12); transform: translateY(-1px); }
  .db-ie-btn-remove { background: rgba(239,68,68,0.12); color:#fca5a5; border:1px solid rgba(239,68,68,0.25); }
  .db-ie-btn-remove:hover { background: rgba(239,68,68,0.22); }
  .db-ie-btn-cancel { background: rgba(255,255,255,0.06); color:#94a3b8; border:1px solid rgba(255,255,255,0.08); }
  .db-ie-btn-cancel:hover { background: rgba(255,255,255,0.1); }
`;

const getPlanInfo = (plan) => {
  const plans = {
    free: { name: 'Free', maxLinks: 20, cls: 'badge-free', features: ['Basic shortening', 'Click tracking', 'QR code (free for a limited time)'] },
    pro: { name: 'Pro', maxLinks: 1000, cls: 'badge-pro', features: ['Custom aliases', 'Expiried & Password protection', 'QR code generation', 'Advanced Analytics dashboard', '5 Brands URL structure', '5 Self-Healing Links (Email alerts)'] },
    business: { name: 'Business', maxLinks: 10000, cls: 'badge-business', features: ['All Pro features', 'Link expiration dates', 'Advanced Analytics dashboard', '100 Brands URL structure', '50 Self-Healing Links (Email alerts)', 'Link Intelligence Score'] },
  };
  return plans[plan] || plans.free;
};

/* ─── component ──────────────────────────────────────────────────────────── */
const Dashboard = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [linksTotal, setLinksTotal] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [clickCounts, setClickCounts] = useState({});
  const [error, setError] = useState('');
  const [upgradeSuccess, setUpgradeSuccess] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTIONS.DESC);
  const [linksPage, setLinksPage] = useState(1);
  const LINKS_PER_PAGE = 10;
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedLinkForQR, setSelectedLinkForQR] = useState(null);
  const [linkScores, setLinkScores] = useState({});
  const [clickMapTab, setClickMapTab] = useState('geo');   // 'geo' | 'date' | 'hourly'
  const [geoRange, setGeoRange] = useState(30);    // days for geographic filter
  const [hourlyRange, setHourlyRange] = useState(30);    // days for hourly filter
  const [healingStatus, setHealingStatus] = useState(null);
  const [healingEnrolled, setHealingEnrolled] = useState({});
  const [healingLoading, setHealingLoading] = useState({});
  const [healingStatusMap, setHealingStatusMap] = useState({}); // { linkId: 'healthy'|'healing'|'failed' }

  // Inline edit modals
  const [expiryModal, setExpiryModal] = useState({ open: false, link: null, value: '' });
  const [passwordModal, setPasswordModal] = useState({ open: false, link: null, value: '' });

  // Popup state
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'confirm', onConfirm: null });

  const analysticsUrl = ANALYTICS_ASSET;
  const qrCodeUrl = QR_ASSET;
  const deleteUrl = DELETE_ASSET;
  const copyUrl = COPY_ASSET;

  // Re-fetch links whenever page or sort changes
  useEffect(() => { fetchLinks(); }, [linksPage, sortField, sortDirection]);
  useEffect(() => { fetchAnalytics(geoRange, hourlyRange); fetchClickCounts(); fetchHealingStatus(); fetchHealingStatuses(); }, []);
  // Re-fetch analytics when geo or hourly range changes
  useEffect(() => { fetchAnalytics(geoRange, hourlyRange); }, [geoRange, hourlyRange]);

  // Sync healingEnrolled when links load
  useEffect(() => {
    if (!links.length) return;
    const map = {};
    links.forEach(l => { map[l.id] = l.healing_enrolled || false; });
    setHealingEnrolled(prev => ({ ...prev, ...map }));
  }, [links]);

  // Fetch link scores for Business plan users
  useEffect(() => {
    if (user?.plan !== 'business' || links.length === 0) return;
    links.forEach(link => {
      if (linkScores[link.id]) return; // already fetched
      axios.get(`${API_BASE_URL}/api/links/${link.id}/score`)
        .then(r => setLinkScores(prev => ({ ...prev, [link.id]: r.data })))
        .catch(() => { }); // silently skip
    });
  }, [links, user?.plan]);

  // All columns are sorted server-side — the backend supports full ORDER BY for all fields
  const fetchLinks = async () => {
    try {
      const params = new URLSearchParams({
        page: linksPage,
        limit: LINKS_PER_PAGE,
        sortField,
        sortDir: sortDirection,
      });
      const r = await axios.get(`${API_BASE_URL}/api/links?${params}`);
      setLinks(r.data.links);
      setLinksTotal(r.data.total);
    }
    catch { setError('Failed to fetch links'); }
  };

  const fetchClickCounts = async () => {
    try { const r = await axios.get(`${API_BASE_URL}/api/analytics/clicks/batch`); setClickCounts(r.data.clickCounts || {}); }
    catch { /* unavailable */ }
  };

  const fetchAnalytics = async (days = 30, hdays = 30) => {
    try { const r = await axios.get(`${API_BASE_URL}/api/analytics/dashboard?days=${days}&hdays=${hdays}`); setAnalytics(r.data); }
    catch { /* may not be available for free users */ }
  };

  const fetchHealingStatus = async () => {
    try { const r = await axios.get(`${API_BASE_URL}/api/healing/status`); setHealingStatus(r.data); }
    catch { /* not available */ }
  };

  const fetchHealingStatuses = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/healing/statuses`);
      setHealingStatusMap(prev => ({ ...prev, ...r.data.statuses }));
    } catch { /* not available */ }
  };

  const toggleHealEnrol = async (link) => {
    const isEnrolled = healingEnrolled[link.id];
    setHealingLoading(prev => ({ ...prev, [link.id]: true }));
    try {
      if (isEnrolled) {
        await axios.delete(`${API_BASE_URL}/api/healing/link/${link.id}/enrol`);
        setHealingEnrolled(prev => ({ ...prev, [link.id]: false }));
        setHealingStatusMap(prev => { const n = { ...prev }; delete n[link.id]; return n; });
      } else {
        await axios.post(`${API_BASE_URL}/api/healing/link/${link.id}/enrol`);
        setHealingEnrolled(prev => ({ ...prev, [link.id]: true }));
      }
      fetchHealingStatus();
      fetchHealingStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Self-Healing: ' + (err.message || 'Failed to update'));
    } finally {
      setHealingLoading(prev => ({ ...prev, [link.id]: false }));
    }
  };

  // ── Inline expiry update ──
  const openExpiryModal = (link) => {
    let v = '';
    if (link.expires_at) {
      // Use local date components to avoid UTC↔local timezone shift
      const d = new Date(link.expires_at);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      v = `${yyyy}-${mm}-${dd}`;
    }
    setExpiryModal({ open: true, link, value: v });
  };

  const handleUpdateExpiry = async (newDate) => {
    const { link } = expiryModal;
    setExpiryModal(m => ({ ...m, open: false }));
    try {
      await axios.put(`${API_BASE_URL}/api/links/${link.id}`, { expiresAt: newDate || null });
      // Patch state immediately for instant UI feedback, then sync from server
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, expires_at: newDate || null } : l));
      fetchLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update expiry date');
    }
  };

  // ── Inline password update ──
  const openPasswordModal = (link) => {
    setPasswordModal({ open: true, link, value: '' });
  };

  const handleUpdatePassword = async (password) => {
    const { link } = passwordModal;
    setPasswordModal(m => ({ ...m, open: false }));
    try {
      await axios.put(`${API_BASE_URL}/api/links/${link.id}`, { password: password ?? '' });
      // Patch immediately for instant UI feedback, then sync from server
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, password_hash: password ? '__set__' : null } : l));
      fetchLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password');
    }
  };

  const handleDelete = async (id) => {
    setPopup({
      isOpen: true,
      title: 'Delete Link',
      message: 'Are you sure you want to delete this link? This action will permanently remove the link and all its analytics data.',
      type: 'danger',
      onConfirm: async () => {
        setPopup(p => ({ ...p, isOpen: false }));
        try {
          await axios.delete(`${API_BASE_URL}/api/links/${id}`);
          // If we just deleted the last item on a non-first page, go back one
          const newTotal = linksTotal - 1;
          const maxPage = Math.max(1, Math.ceil(newTotal / LINKS_PER_PAGE));
          if (linksPage > maxPage) {
            setLinksPage(maxPage); // triggers fetchLinks via useEffect
          } else {
            fetchLinks();
          }
          fetchAnalytics();
          fetchClickCounts();
        }
        catch { setError('Failed to delete link'); }
      }
    });
  };

  const toggleStatus = async (link) => {
    if (!isPro) return; // Feature not available for free users
    try {
      const newStatus = !link.is_active;
      await axios.put(`${API_BASE_URL}/api/links/${link.id}`, { isActive: newStatus });

      // Update local state to reflect change without full refresh
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, is_active: newStatus } : l));

      // Also refresh dashboard stats in case it affects active link counts
      fetchAnalytics();
    } catch {
      setError('Failed to update link status');
    }
  };

  const handleSort = (field) => {
    const newDir = sortField === field && sortDirection === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC;
    setSortField(field);
    setSortDirection(newDir);
    setLinksPage(1); // reset to first page on sort change — useEffect triggers fetchLinks
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === SORT_DIRECTIONS.ASC ? ' ↑' : ' ↓';
  };

  // All sorting is server-side — displayLinks is simply the paginated result from the API
  const displayLinks = links;

  // Total pages from server-reported count (or client count for clicks sort)
  const linksTotalPages = Math.max(1, Math.ceil(linksTotal / LINKS_PER_PAGE));

  const openQrModal = (link) => { setSelectedLinkForQR(link); setQrModalOpen(true); };
  const closeQrModal = () => { setQrModalOpen(false); setSelectedLinkForQR(null); };

  const copyToClipboard = async (text, label = 'URL') => {
    try { await navigator.clipboard.writeText(text); }
    catch { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
    setPopup({
      isOpen: true,
      title: 'Copied!',
      message: `${label} has been copied to your clipboard.`,
      type: 'success'
    });
  };

  const shareUrl = async (url) => {
    if (navigator.share) { try { await navigator.share({ title: 'RDRTO URL', text: 'Check out this link!', url }); return; } catch { } }
    copyToClipboard(url);
  };

  // Returns the full short URL for a link, respecting brand path / subdomain types
  const getShortUrl = (link) => {
    const brand = link?.brand_name;
    if (link?.url_type === 'brand_path' && brand) {
      return `${API_BASE_URL}/${brand}/${link.short_code}`;
    } else if (link?.url_type === 'brand_subdomain' && brand) {
      // In local dev, subdomain DNS (brand.localhost) doesn't resolve — use path format instead
      if (isLocal) return `${API_BASE_URL}/${brand}/${link.short_code}`;
      return API_BASE_URL.replace('://', `://${brand}.`) + `/${link.short_code}`;
    }
    return `${API_BASE_URL}/${link.short_code}`;
  };

  // Returns the display label for the short URL (no protocol)
  const getShortUrlLabel = (link) => {
    const brand = link?.brand_name;
    if (link?.url_type === 'brand_path' && brand) {
      return `rdrto.com/${brand}/${link.short_code}`;
    } else if (link?.url_type === 'brand_subdomain' && brand) {
      // Always show the pretty branded label regardless of dev/prod
      return `${brand}.rdrto.com/${link.short_code}`;
    }
    return `rdrto.com/${link.short_code}`;
  };

  const downloadQR = () => {
    const a = document.createElement('a');
    a.href = `${API_BASE_URL}/api/qr/${selectedLinkForQR.short_code}`;
    a.download = `qr-${selectedLinkForQR.short_code}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const planInfo = getPlanInfo(user?.plan);
  const isPro = user?.plan === 'pro' || user?.plan === 'business';
  const healLimit = user?.plan === 'business' ? 50 : user?.plan === 'pro' ? 5 : 0;

  return (
    <div className="db-page">
      <style>{STYLES}</style>
      <div className="db-inner">

        {/* ── Page heading ── */}
        <div className="db-heading">
          <h1 className="db-title">Dashboard</h1>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {(user?.plan === 'pro' || user?.plan === 'business') && (
              <Link to="/brand-settings" className="db-btn-brand">🏷️ Brand Settings</Link>
            )}
            <Link to="/create" className="db-btn-primary">+ Create New Link</Link>
          </div>
        </div>

        {/* ── Alerts ── */}
        {error && <div className="db-alert db-alert-error">{error}</div>}
        {upgradeSuccess && <div className="db-alert db-alert-success">✅ {upgradeSuccess}</div>}

        {/* ── Top cards row ── */}
        <div className="db-stat-row">

          {/* Click Map card — tabbed */}
          <div className="db-card" style={{ gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 0 }}>
              <p className="db-card-title" style={{ margin: 0 }}>Click Map</p>
              <div className="db-chart-tabs">
                <button
                  className={`db-chart-tab${clickMapTab === 'geo' ? ' active' : ''}`}
                  onClick={() => setClickMapTab('geo')}
                >🌍 Geographic</button>
                <button
                  className={`db-chart-tab${clickMapTab === 'date' ? ' active' : ''}`}
                  onClick={() => setClickMapTab('date')}
                  disabled={!isPro}
                  title={!isPro ? "Upgrade to PRO for Date Analytics" : ""}
                >📈 Date vs Clicks</button>
                <button
                  className={`db-chart-tab${clickMapTab === 'hourly' ? ' active' : ''}`}
                  onClick={() => setClickMapTab('hourly')}
                  disabled={!isPro}
                  title={!isPro ? "Upgrade to PRO for Hourly Analytics" : ""}
                >🕐 Hour of Day</button>
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              {clickMapTab === 'geo' && <GeographicChart analytics={analytics} geoRange={geoRange} setGeoRange={setGeoRange} />}
              {clickMapTab === 'date' && <DateClicksChart data={analytics?.clicksByDate} />}
              {clickMapTab === 'hourly' && <HourlyChart data={analytics?.hourlyData} hourlyRange={hourlyRange} setHourlyRange={setHourlyRange} />}
            </div>
          </div>

          {/* Plan info card */}
          {user && (
            <div className="db-card">
              <p className="db-card-title">Your Plan</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <p className="db-plan-name" style={{ margin: 0 }}>{planInfo.name} Plan</p>
                <span className={`db-plan-badge ${planInfo.cls}`}>{planInfo.name}</span>
              </div>
              <p className="db-plan-limit">Up to {planInfo.maxLinks.toLocaleString()} links</p>
              <ul className="db-feature-list">
                {planInfo.features.map(f => <li key={f}>{f}</li>)}
              </ul>
              {user.plan === 'free' && (
                <Link to="/upgrade" className="db-btn-primary" style={{ width: '80%', justifyContent: 'center' }}>
                  Upgrade to Pro / Business
                </Link>
              )}
              {user.plan === 'pro' && (
                <Link to="/upgrade" className="db-btn-primary" style={{ width: '80%', justifyContent: 'center', background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 4px 14px rgba(245,158,11,0.35)' }}>
                  ⭐ Upgrade to Business
                </Link>
              )}
            </div>
          )}

          {/* Analytics overview card */}
          {analytics && (
            <div className="db-card">
              <p className="db-card-title">Overview</p>
              <div className="db-stats-inner">
                <div className="db-stat-tile">
                  <div className="db-stat-num">{analytics.totalLinks}</div>
                  <div className="db-stat-label">Total Links</div>
                </div>
                <div className="db-stat-tile">
                  <div className="db-stat-num">{analytics.totalClicks}</div>
                  <div className="db-stat-label">Total Clicks</div>
                </div>
              </div>

              {/* Self-Healing summary (Pro/Business only) */}
              {isPro && (
                <>
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '16px 0 12px' }} />
                  <p className="db-card-title" style={{ color: '#10b981', margin: '0 0 10px', fontSize: 12 }}>🏥 Self-Healing</p>
                  {healingStatus && parseInt(healingStatus.enrolled) > 0 ? (
                    <>
                      <div className="db-stats-inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                        <div className="db-stat-tile" style={{ padding: '8px 4px', textAlign: 'center' }}>
                          <div className="db-stat-num" style={{ fontSize: '1.2rem' }}>{healingStatus.healthy || 0}</div>
                          <div className="db-stat-label" style={{ fontSize: 10 }}>✅ Healthy</div>
                        </div>
                        <div className="db-stat-tile" style={{ padding: '8px 4px', textAlign: 'center' }}>
                          <div className="db-stat-num" style={{ fontSize: '1.2rem', color: '#fde047' }}>{healingStatus.healing || 0}</div>
                          <div className="db-stat-label" style={{ fontSize: 10 }}>🔄 Healing</div>
                        </div>
                        <div className="db-stat-tile" style={{ padding: '8px 4px', textAlign: 'center' }}>
                          <div className="db-stat-num" style={{ fontSize: '1.2rem', color: '#fca5a5' }}>{healingStatus.failed || 0}</div>
                          <div className="db-stat-label" style={{ fontSize: 10 }}>❌ Failed</div>
                        </div>
                      </div>
                      <p className="db-heal-last" style={{ marginTop: 8 }}>{healingStatus.enrolled}/{healLimit} enrolled{healingStatus.last_checked_at ? ` · ${new Date(healingStatus.last_checked_at).toLocaleTimeString()}` : ''}</p>
                    </>
                  ) : (
                    <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.55 }}>
                      Enable healing on any link below.<br />
                      <span style={{ color: '#334155', fontSize: 11 }}>Up to {healLimit} links on your {user?.plan} plan.</span>
                    </p>
                  )}
                </>
              )}
            </div>
          )}

        </div>

        {/* ── Links table ── */}
        <div className="db-links-card">
          <div className="db-links-header">
            <h2 className="db-links-title">Your Links <span style={{ color: '#475569', fontWeight: 400, fontSize: '0.85em' }}>({linksTotal})</span></h2>
          </div>

          {links.length === 0 ? (
            <div className="db-empty">
              <div className="db-empty-icon">🔗</div>
              <p>No links yet. Create your first short link above!</p>
            </div>
          ) : (
            <>
              <div className="db-table-wrap">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('short_code')}>Short URL{getSortIcon('short_code')}</th>
                      <th onClick={() => handleSort('long_url')}>Destination{getSortIcon('long_url')}</th>
                      <th onClick={() => handleSort('clicks')}>Clicks{getSortIcon('clicks')}</th>
                      <th onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
                      {user?.plan === 'business' && <th onClick={() => handleSort('score')}>Score{getSortIcon('score')}</th>}
                      {isPro && <th onClick={() => handleSort('heal')}>Heal{getSortIcon('heal')}</th>}
                      <th onClick={() => handleSort('created_at')}>Created{getSortIcon('created_at')}</th>
                      {isPro && <th onClick={() => handleSort('expires_at')}>Expires{getSortIcon('expires_at')}</th>}
                      {isPro && <th onClick={() => handleSort('password_hash')}>P/W{getSortIcon('password_hash')}</th>}
                      {isPro && <th onClick={() => handleSort('url_type')}>Type{getSortIcon('url_type')}</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayLinks.map(link => (
                      <tr key={link.id}>
                        <td>
                          {(() => {
                            const brand = link.brand_name;
                            if (link.url_type === 'brand_path' && brand) {
                              return <a className="db-link-short" href={`${API_BASE_URL}/${brand}/${link.short_code}`} target="_blank" rel="noopener noreferrer">rdrto.com/{brand}/{link.short_code}</a>;
                            } else if (link.url_type === 'brand_subdomain' && brand) {
                              // href uses path format in dev (subdomain DNS won't resolve locally)
                              // but always display the pretty branded label
                              const href = isLocal
                                ? `${API_BASE_URL}/${brand}/${link.short_code}`
                                : API_BASE_URL.replace('://', `://${brand}.`) + `/${link.short_code}`;
                              return <a className="db-link-short" href={href} target="_blank" rel="noopener noreferrer">{brand}.rdrto.com/{link.short_code}</a>;
                            }
                            return <a className="db-link-short" href={`${API_BASE_URL}/${link.short_code}`} target="_blank" rel="noopener noreferrer">rdrto.com/{link.short_code}</a>;
                          })()}
                        </td>
                        <td>
                          <a className="db-link-long" href={link.long_url} target="_blank" rel="noopener noreferrer" title={link.long_url}>
                            {link.long_url}
                          </a>
                        </td>
                        <td><span className="db-click-badge">{clickCounts[link.id] || 0}</span></td>
                        <td>
                          {(() => {
                            // Compare YYYY-MM-DD local strings to avoid UTC timezone shift
                            const localDateStr = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };
                            const expired = link.expires_at && localDateStr(link.expires_at) < localDateStr(new Date());
                            const active = link.is_active && !expired;
                            const isClickable = isPro && !expired;

                            return (
                              <div
                                className={`db-status-badge ${expired ? 'db-status-expired' : active ? 'db-status-active' : 'db-status-inactive'} ${isClickable ? 'clickable' : ''} ${(!isPro && !expired) ? 'disabled' : ''}`}
                                onClick={() => isClickable && toggleStatus(link)}
                                title={isClickable ? (active ? "Click to Deactivate" : "Click to Activate") : (!isPro && !expired ? "Upgrade to PRO to toggle status" : "")}
                              >
                                {expired ? 'Expired' : active ? 'Active' : 'Inactive'}
                              </div>
                            );
                          })()}
                        </td>
                        {user?.plan === 'business' && (
                          <td>
                            {linkScores[link.id] ? (
                              <span className={`db-score-badge db-score-${linkScores[link.id].color}`}>
                                {linkScores[link.id].score}
                                {linkScores[link.id].color === 'green' ? ' 🟢' : linkScores[link.id].color === 'yellow' ? ' 🟡' : ' 🔴'}
                              </span>
                            ) : (
                              <span className="db-score-badge db-score-loading">···</span>
                            )}
                          </td>
                        )}
                        {isPro && (() => {
                          const enrolled = healingEnrolled[link.id];
                          const hs = healingStatusMap[link.id];
                          const loading = healingLoading[link.id];
                          const label = loading ? '…'
                            : !enrolled ? '+ Heal'
                              : hs === 'healthy' ? 'Healthy'
                                : hs === 'failed' ? 'Failed'
                                  : hs === 'healing' ? 'Healing'
                                    : 'ON';
                          const cls = !enrolled ? 'db-heal-toggle-off'
                            : hs === 'failed' ? 'db-status-inactive'
                              : hs === 'healing' ? 'db-status-expired'
                                : 'db-status-active';
                          return (
                            <td>
                              <button
                                className={`db-status-badge ${cls} clickable`}
                                onClick={() => toggleHealEnrol(link)}
                                disabled={loading || healLimit === 0}
                                title={enrolled ? 'Click to disable healing' : 'Click to enable healing'}
                                style={{ border: 'none', cursor: loading || healLimit === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
                              >
                                {label}
                              </button>
                            </td>
                          );
                        })()}
                        <td>{new Date(link.created_at).toLocaleDateString()}</td>
                        {isPro && (
                          <td>
                            <button
                              className={`db-cell-edit-btn${link.expires_at ? ' has-value' : ''}`}
                              onClick={() => openExpiryModal(link)}
                              title="Click to set or change the expiry date"
                            >
                              🗓️ {link.expires_at ? new Date(link.expires_at).toLocaleDateString() : 'Never'}
                            </button>
                          </td>
                        )}
                        {isPro && (
                          <td>
                            <button
                              className={`db-cell-edit-btn${link.password_hash ? ' has-pw' : ''}`}
                              onClick={() => openPasswordModal(link)}
                              title={link.password_hash ? 'Click to change or remove password' : 'Click to set a password'}
                            >
                              {link.password_hash ? '🔒 Set' : '🔓 None'}
                            </button>
                          </td>
                        )}
                        {isPro && (
                          <td>
                            {link.url_type === 'brand_path' && <span className="db-url-type-badge db-url-type-path">PATH</span>}
                            {link.url_type === 'brand_subdomain' && <span className="db-url-type-badge db-url-type-sub">SUB</span>}
                            {(!link.url_type || link.url_type === 'standard') && <span style={{ color: '#334155' }}>—</span>}
                          </td>
                        )}
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {/* Copy short URL */}
                            <button className="db-action-btn" title="Copy Short URL" onClick={() => copyToClipboard(getShortUrl(link))}>
                              <img src={copyUrl} alt="Copy" />
                            </button>

                            {/* Analytics */}
                            {isPro ? (
                              <Link to={`/analytics/${link.id}`}>
                                <button className="db-action-btn" title="View Analytics">
                                  <img src={analysticsUrl} alt="Analytics" />
                                </button>
                              </Link>
                            ) : (
                              <button className="db-action-btn" disabled title="Upgrade to PRO for Analytics">
                                <img src={analysticsUrl} alt="Analytics" />
                              </button>
                            )}

                            {/* QR */}
                            <button className="db-action-btn" title="Show QR Code" onClick={() => openQrModal(link)}>
                              <img src={qrCodeUrl} alt="QR Code" />
                            </button>

                            {/* Delete */}
                            <button className="db-action-btn" title="Delete Link" onClick={() => handleDelete(link.id)}>
                              <img src={deleteUrl} alt="Delete" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {linksTotalPages > 1 && (
                <div className="db-links-pagination">
                  <span className="db-links-page-info">
                    Page {linksPage} of {linksTotalPages}
                  </span>
                  <div className="db-links-page-btns">
                    <button
                      className="db-links-page-btn"
                      onClick={() => setLinksPage(p => Math.max(1, p - 1))}
                      disabled={linksPage === 1}
                    >
                      ← Prev
                    </button>
                    <button
                      className="db-links-page-btn"
                      onClick={() => setLinksPage(p => Math.min(linksTotalPages, p + 1))}
                      disabled={linksPage === linksTotalPages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── QR Modal ── */}
        {qrModalOpen && selectedLinkForQR && (() => {
          const qrShortUrl = getShortUrl(selectedLinkForQR);
          const qrShortLabel = getShortUrlLabel(selectedLinkForQR);
          return (
            <div className="db-modal-overlay" onClick={closeQrModal}>
              <div className="db-modal" onClick={e => e.stopPropagation()}>
                <h3>QR Code</h3>
                <p className="db-modal-url">{qrShortLabel}</p>
                <img
                  src={`${API_BASE_URL}/api/qr/${selectedLinkForQR.short_code}`}
                  alt={`QR for ${selectedLinkForQR.short_code}`}
                />
                <p className="db-modal-hint">Scan to open the shortened link</p>
                <div className="db-modal-actions">
                  <button className="db-modal-btn db-modal-btn-copy" onClick={() => copyToClipboard(qrShortUrl)}>📋 Copy URL</button>
                  <button className="db-modal-btn db-modal-btn-share" onClick={() => shareUrl(qrShortUrl)}>📤 Share</button>
                  <button className="db-modal-btn db-modal-btn-dl" onClick={downloadQR}>💾 Download QR</button>
                </div>
                <button className="db-modal-btn db-modal-btn-close" onClick={closeQrModal} style={{ marginTop: 14, width: '100%' }}>Close</button>
              </div>
            </div>
          );
        })()}

        {/* ── Expiry Date Modal ── */}
        {expiryModal.open && expiryModal.link && (
          <div className="db-ie-overlay" onClick={() => setExpiryModal(m => ({ ...m, open: false }))}>
            <div className="db-ie-modal" onClick={e => e.stopPropagation()}>
              <h3>📅 Set Expiry Date</h3>
              <p className="db-modal-url">{getShortUrlLabel(expiryModal.link)}</p>
              <input
                className="db-ie-input"
                type="date"
                value={expiryModal.value}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setExpiryModal(m => ({ ...m, value: e.target.value }))}
              />
              <div className="db-ie-actions">
                <button
                  className="db-ie-btn db-ie-btn-save"
                  disabled={!expiryModal.value}
                  onClick={() => handleUpdateExpiry(expiryModal.value)}
                >✓ Save</button>
                <button
                  className="db-ie-btn db-ie-btn-remove"
                  onClick={() => handleUpdateExpiry(null)}
                >🗑 Clear Date</button>
                <button
                  className="db-ie-btn db-ie-btn-cancel"
                  onClick={() => setExpiryModal(m => ({ ...m, open: false }))}
                >Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Password Modal ── */}
        {passwordModal.open && passwordModal.link && (
          <div className="db-ie-overlay" onClick={() => setPasswordModal(m => ({ ...m, open: false }))}>
            <div className="db-ie-modal" onClick={e => e.stopPropagation()}>
              <h3>🔑 Set Password</h3>
              <p className="db-modal-url">{getShortUrlLabel(passwordModal.link)}</p>
              <input
                className="db-ie-input"
                type="password"
                placeholder="Enter new password…"
                value={passwordModal.value}
                autoFocus
                onChange={e => setPasswordModal(m => ({ ...m, value: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter' && passwordModal.value) handleUpdatePassword(passwordModal.value); }}
              />
              <div className="db-ie-actions">
                <button
                  className="db-ie-btn db-ie-btn-save"
                  disabled={!passwordModal.value}
                  onClick={() => handleUpdatePassword(passwordModal.value)}
                >✓ Save</button>
                <button
                  className="db-ie-btn db-ie-btn-remove"
                  onClick={() => handleUpdatePassword('')}
                >🗑 Remove PW</button>
                <button
                  className="db-ie-btn db-ie-btn-cancel"
                  onClick={() => setPasswordModal(m => ({ ...m, open: false }))}
                >Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Professional Popup component for confirmation/alerts */}
        <Popup
          isOpen={popup.isOpen}
          title={popup.title}
          message={popup.message}
          type={popup.type}
          onConfirm={popup.onConfirm}
          onCancel={() => setPopup(p => ({ ...p, isOpen: false }))}
        />

      </div>
    </div>
  );
};

export default Dashboard;
